<script setup>
import { computed } from 'vue';
import { SMS_CONSENT_STATUS, SMS_CONSENT_CHIP_CONFIG, SMS_CONSENT_METHOD_LABELS } from '@/fhirworks/constants/smsConsent';

/**
 * Read-only 4-state SMS reminder consent chip (issue #4071, FR-5).
 *
 * Informational only: clicking opens a small popover with a per-status message — no dialog,
 * no actions. Used on the mobile phone list item in the edit patient dialog and mirrored
 * next to the patient name in the appointment dialog.
 */
const props = defineProps({
    status: {
        type: String,
        default: SMS_CONSENT_STATUS.NOT_ASKED,
    },
    patientName: {
        type: String,
        default: 'This patient',
    },
    // ISO timestamp of the latest consent change (smsReminderConsent.lastUpdatedAt).
    date: {
        type: String,
        default: null,
    },
    // Capture method of the latest change (smsReminderConsent.method).
    method: {
        type: String,
        default: null,
    },
    // Clinic's dedicated SMS number, shown in the carrier opt-out message when known.
    clinicNumber: {
        type: String,
        default: null,
    },
});

const config = computed(() => SMS_CONSENT_CHIP_CONFIG[props.status] || SMS_CONSENT_CHIP_CONFIG[SMS_CONSENT_STATUS.NOT_ASKED]);

const formattedDate = computed(() => {
    if (!props.date) return '';
    const parsed = new Date(props.date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
});

const message = computed(() => {
    const who = props.patientName;
    const when = formattedDate.value;
    const methodLabel = props.method ? SMS_CONSENT_METHOD_LABELS[props.method] || props.method : '';
    const via = methodLabel ? ` via ${methodLabel}` : '';

    switch (props.status) {
        case SMS_CONSENT_STATUS.OPTED_IN:
            return `${who} opted in${when ? ` on ${when}` : ''}${via}.`;
        case SMS_CONSENT_STATUS.DECLINED:
            return `${who} declined${when ? ` on ${when}` : ''}. Update in the patient's demographics.`;
        case SMS_CONSENT_STATUS.OPTED_OUT_CARRIER: {
            const resume = props.clinicNumber ? ` To resume, they must text START to ${props.clinicNumber} from their phone.` : ` To resume, they must text START from their phone.`;
            return `${who} opted out${when ? ` on ${when}` : ''}.${resume}`;
        }
        case SMS_CONSENT_STATUS.NOT_ASKED:
        default:
            return "SMS reminder consent has not been captured. Update in the patient's demographics.";
    }
});
</script>

<template>
    <v-menu location="bottom start" :close-on-content-click="false">
        <template #activator="{ props: menuProps }">
            <v-chip v-bind="menuProps" :color="config.color" size="x-small" variant="tonal" class="font-weight-medium" role="button">
                <font-awesome-icon :icon="['far', config.icon]" class="mr-1" />
                {{ config.label }}
            </v-chip>
        </template>
        <v-card max-width="280">
            <v-card-text class="text-body-2">{{ message }}</v-card-text>
        </v-card>
    </v-menu>
</template>
